const SCHEMA_VERSION_KEY = '_opsSchemaVersion';

type SavedColumnState = {
  dataField?: string;
  name?: string;
  visible?: boolean;
  visibleIndex?: number;
  width?: number | string;
  filterValue?: unknown;
  filterValues?: unknown;
  selectedFilterOperation?: string;
};

export type OpsGridStateStorageOptions = {
  storageKey: string;
  schemaVersion: number;
  /** Stable column identifiers (`dataField` or `name` for each data column). */
  columns: readonly string[];
};

function columnKey(column: SavedColumnState): string | null {
  const key = column.dataField ?? column.name;
  return typeof key === 'string' && key.length > 0 ? key : null;
}

function stripColumnFilters(column: SavedColumnState): SavedColumnState {
  const { filterValue, filterValues, selectedFilterOperation, ...rest } = column;
  return rest;
}

export function sanitizeOpsGridState(
  state: Record<string, unknown> | null,
  schemaVersion: number,
  allowedColumns: readonly string[]
): Record<string, unknown> | null {
  if (!state) return null;

  const allowed = new Set(allowedColumns);
  const savedVersion = typeof state[SCHEMA_VERSION_KEY] === 'number' ? state[SCHEMA_VERSION_KEY] : 0;
  const schemaMismatch = savedVersion !== schemaVersion;

  const next: Record<string, unknown> = { ...state };
  next[SCHEMA_VERSION_KEY] = schemaVersion;

  if (schemaMismatch) {
    delete next.filterValue;
    delete next.filterPanel;
    delete next.searchText;
    delete next.searchPanel;
  }

  if (Array.isArray(state.columns)) {
    const known = (state.columns as SavedColumnState[]).filter((column) => {
      const key = columnKey(column);
      return key !== null && allowed.has(key);
    });

    const sanitized = schemaMismatch ? known.map(stripColumnFilters) : known;
    sanitized.sort((a, b) => Number(a.visibleIndex ?? 0) - Number(b.visibleIndex ?? 0));
    sanitized.forEach((column, index) => {
      column.visibleIndex = index;
    });
    next.columns = sanitized;
  }

  return next;
}

export function readOpsGridState(storageKey: string): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function writeOpsGridState(storageKey: string, state: Record<string, unknown>): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // quota / private mode
  }
}

export function createOpsGridStateStoringProps({
  storageKey,
  schemaVersion,
  columns
}: OpsGridStateStorageOptions) {
  return {
    enabled: true,
    type: 'custom' as const,
    savingTimeout: 500,
    customLoad: () => {
      const raw = readOpsGridState(storageKey);
      return Promise.resolve(sanitizeOpsGridState(raw, schemaVersion, columns));
    },
    customSave: (state: Record<string, unknown>) => {
      const saved = sanitizeOpsGridState(state, schemaVersion, columns);
      if (saved) writeOpsGridState(storageKey, saved);
      return Promise.resolve();
    }
  };
}
